import React, { useState, useEffect } from 'react'
import { updateTask, updateSubtask } from '../utils/supabase'
import { useAuth } from '../hooks/useAuth'

const TaskItem = ({ task, isOpen, onToggleOpen, onEdit, onShare, onDelete, onTaskUpdate }) => {
  const { user } = useAuth()
  const [timeLeft, setTimeLeft] = useState('')
  const [showTimeLeft, setShowTimeLeft] = useState(false)

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    const weekday = date.toLocaleDateString('en-US', { weekday: 'short' })
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
    const timeStr = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`
    return `${dateStr} ${weekday} ${timeStr}`
  }

  const calculateTimeLeft = (deadline) => {
    const now = new Date()
    const end = new Date(deadline)
    const diff = end - now

    if (diff <= 0) return 'Expired'

    const days = Math.floor(diff / (1000 * 60 * 60 * 24))
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((diff % (1000 * 60)) / 1000)

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`
    }
    
    return `${hours}h ${minutes}m ${seconds}s`
  }

  useEffect(() => {
    if (showTimeLeft) {
      const timer = setInterval(() => {
        setTimeLeft(calculateTimeLeft(task.deadline))
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [showTimeLeft, task.deadline])

  const getTaskStatus = () => {
    if (task.completed) return 'completed'
    
    const now = new Date()
    const end = new Date(task.deadline)
    
    if (now > end) return 'expired-incomplete' // Incomplete expired tasks get rotting green
    if (end - now <= 2 * 24 * 60 * 60 * 1000) return 'overdue' // 2 days
    return 'active'
  }

  const getProgressPercentage = () => {
    const start = new Date(task.created_at)
    const end = new Date(task.deadline)
    const now = new Date()
    
    const total = end - start
    const elapsed = now - start
    
    return Math.min(100, Math.max(0, (elapsed / total) * 100))
  }

  const handleToggleComplete = async (e) => {
    e.stopPropagation()
    
    // Optimistic update
    const updatedTask = { ...task, completed: !task.completed }
    onTaskUpdate(updatedTask)
    
    try {
      await updateTask(task.id, { completed: !task.completed })
    } catch (error) {
      // Rollback on error
      onTaskUpdate({ ...task, completed: task.completed })
      console.error('Error updating task:', error)
    }
  }

  const handleSubtaskToggle = async (subtaskId, currentDone) => {
    const timestamp = !currentDone ? new Date().toISOString() : null
    
    // Optimistic update
    const updatedSubtasks = task.subtasks.map(subtask =>
      subtask.id === subtaskId 
        ? { ...subtask, done: !currentDone, completed_at: timestamp }
        : subtask
    )
    const updatedTask = { ...task, subtasks: updatedSubtasks }
    onTaskUpdate(updatedTask)
    
    try {
      await updateSubtask(subtaskId, { 
        done: !currentDone,
        completed_at: timestamp
      })
    } catch (error) {
      // Rollback on error
      onTaskUpdate(task)
      console.error('Error updating subtask:', error)
    }
  }

  const handleDateClick = (e) => {
    e.stopPropagation()
    if (showTimeLeft) {
      setShowTimeLeft(false)
    } else {
      setTimeLeft(calculateTimeLeft(task.deadline))
      setShowTimeLeft(true)
    }
  }

  const status = getTaskStatus()
  const progressPercentage = getProgressPercentage()
  
  const isTaskCreator = task.user_id === user?.id
  const isSharedWithMe = task.task_shares?.some(share => 
    share.shared_with_email === user?.email
  )

  const taskBarClass = `task-bar ${isOpen ? 'opened' : ''} ${status}`

  return (
    <div>
      <div 
        className={taskBarClass}
        onClick={onToggleOpen}
      >
        <div className="task-header">
          <div className="task-info">
            <div className="info">
              <div className="title">
                {task.title}
                {isSharedWithMe && <span className="shared-indicator"> (shared)</span>}
              </div>
              <div className="task-actions">
                <button 
                  className="action-btn" 
                  onClick={handleToggleComplete}
                  title={task.completed ? 'Mark as incomplete' : 'Mark as complete'}
                >
                  {task.completed ? '↩️' : '✅'}
                </button>
                <button 
                  className="action-btn" 
                  onClick={(e) => {
                    e.stopPropagation()
                    onEdit(task)
                  }}
                  title="Edit task"
                >
                  ✏️
                </button>
                {isTaskCreator && (
                  <button 
                    className="action-btn" 
                    onClick={(e) => {
                      e.stopPropagation()
                      onShare(task)
                    }}
                    title="Share task"
                  >
                    👥
                  </button>
                )}
                <button 
                  className="action-btn" 
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(task)
                  }}
                  title={isTaskCreator ? "Delete task" : "Remove me from task"}
                >
                  {isTaskCreator ? '🗑️' : '👋'}
                </button>
              </div>
            </div>
            <div className="priority-circles">
              {[1, 2, 3, 4, 5].map(i => (
                <span 
                  key={i} 
                  className={i <= task.priority ? 'active' : ''}
                ></span>
              ))}
            </div>
          </div>
          <div className="end-date" onClick={handleDateClick}>
            {showTimeLeft ? timeLeft : formatDate(task.deadline)}
          </div>
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill" 
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {isOpen && (
        <div className={`task-details active ${status}`}>
          <div>
            <strong>Creator:</strong> {task.creator_email || 'Unknown'} 
            <em> ({formatDate(task.created_at)})</em>
          </div>
          <div className="creator-shared">
            <strong>Shared With:</strong>{' '}
            {task.task_shares?.length > 0 
              ? task.task_shares.map(share => share.shared_with_email).join(', ')
              : '—'
            }
          </div>
          <div style={{ marginTop: '8px' }}>
            <strong>Description:</strong> {task.description || '—'}
          </div>
          
          {task.subtasks && task.subtasks.length > 0 && (
            <div className="subtasks">
              <strong>Subtasks:</strong>
              <ul>
                {task.subtasks.map(subtask => (
                  <li key={subtask.id}>
                    <label>
                      <input
                        type="checkbox"
                        checked={subtask.done}
                        onChange={() => handleSubtaskToggle(subtask.id, subtask.done)}
                      />
                      {subtask.title} — <em>{formatDate(subtask.deadline)}</em>
                      {subtask.done && subtask.completed_at && (
                        <small> (done at {formatDate(subtask.completed_at)})</small>
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default TaskItem